import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectOrganizationData } from 'pages/redux';
import { fetchFormsInboxCounts, FormsInboxCounts } from './formsReportsApi';

const REFRESH_EVENT = 'forms_inbox_counts_refresh';
const INBOX_REFRESH_EVENT = 'forms_inbox_refresh';

export const notifyFormsInboxCountsRefresh = () => {
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT));
};

/** Reload Forms sidebar lists (reports, report requests, workflows). */
export const notifyFormsInboxRefresh = () => {
  window.dispatchEvent(new CustomEvent(INBOX_REFRESH_EVENT));
  notifyFormsInboxCountsRefresh();
};

export const useFormsInboxCounts = (enabled = true) => {
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector(selectOrganizationData)?.id;
  // Session restore loads org after the nav rail mounts — skip until hDId is in API headers.
  const canFetch = enabled && Boolean(organizationId);
  const [counts, setCounts] = useState<FormsInboxCounts>({
    openReports: 0,
    openReportRequests: 0,
  });

  const refresh = useCallback(async () => {
    if (!canFetch) return;
    const next = await fetchFormsInboxCounts(dispatch);
    setCounts(next);
  }, [dispatch, canFetch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!canFetch) return undefined;
    const onRefresh = () => { refresh(); };
    window.addEventListener(REFRESH_EVENT, onRefresh);
    window.addEventListener(INBOX_REFRESH_EVENT, onRefresh);
    return () => {
      window.removeEventListener(REFRESH_EVENT, onRefresh);
      window.removeEventListener(INBOX_REFRESH_EVENT, onRefresh);
    };
  }, [canFetch, refresh]);

  return {
    counts,
    badgeTotal: counts.openReports + counts.openReportRequests,
    refresh,
  };
};
